<template>
  <div
    v-if="props.raffle.status !== ContestStatus.Ended || !!finishedData"
    class="bg-surface shadow-xl relative flex flex-1 flex-col"
    :class="{
      'w-[312px] max-w-full min-h-[464px]': !variant || variant === 'regular',
      'w-full ': variant === 'responsive',
      'rounded-3xl': !channelStore.isAsMonacoFanToken,
    }"
  >
    <div
      class="flex flex-col flex-1 shadow-[inset_6px_7px_14px_0px_rgba(0,0,0,0.25)] gap-4 bg-transparent border-[3px] overflow-hidden"
      :class="[
        !channelStore.isAsMonacoFanToken && 'rounded-3xl',
        showCreatorControls || canJoinRaffle ? 'pt-3' : 'py-3',
        {
          'w-full ': variant === 'responsive',
          '!border-border-brand': priority === 'normal',
          '!border-border-warning': priority === 'incoming',
          '!border-border-critical': priority === 'urgent',
        },
        priority === 'unavailable' || finishedData?.winner
          ? '!bg-surface'
          : {
              '!bg-fill-brand-secondary': priority === 'normal',
              '!bg-fill-warning-secondary': priority === 'incoming',
              '!bg-fill-critical-secondary': priority === 'urgent',
            },
      ]"
    >
      <div v-if="finishedData" class="h-full flex flex-col">
        <ContestFinished
          class="flex-1"
          object-name="Raffle"
          :data="finishedData"
          :allow-switch-to-leaderboard="false"
          @delete="onDeleteRaffle"
        >
          <template #actions>
            <IconButtonV2 icon="mdi:ios-share" @click="onShareClick" />
          </template>
        </ContestFinished>
        <!-- Raffle Winner card date -->
        <p
          class="flex flex-col items-center gap-4 w-full text-text font-sans text-sm mt-auto pb-4"
        >
          {{ prettyEndTime }}
        </p>
      </div>

      <RaffleJoin
        v-if="!finishedData && priority !== 'unavailable'"
        ref="raffleJoin"
        class="flex-1"
        :loading="loading"
        :data="raffle as unknown as RaffleJoinData"
        :priority="priority"
        :countdown-data="countdownData"
        :expired="expired"
        :can-join-raffle="canJoinRaffle"
        :user-is-creator="showCreatorControls"
        @join="() => emit('refetch')"
        @delete="onDeleteRaffle"
        @share="onShareClick"
      />

      <div
        v-if="showCreatorControls"
        class="flex flex-col items-center gap-4 p-4 bg-fill-brand-secondary backdrop-blur-[35px]"
        :class="{
          'rounded-standard': !channelStore.isAsMonacoFanToken,
          '!bg-fill-brand-secondary': priority === 'normal',
          '!bg-fill-warning-secondary': priority === 'incoming',
          '!bg-fill-critical-secondary': priority === 'urgent',
        }"
      >
        <div class="flex items-start gap-4 self-stretch font-sans text-sm">
          <ContestButton :priority="priority" @click="onEditClick">
            {{ $t("channel.contests.raffle.actions.edit") }}
          </ContestButton>

          <ContestButton
            v-if="raffle.status === ContestStatus.Draft"
            :priority="priority"
            :is-loading="loading || loadingStartRaffle"
            @click="showPublishConfirmation = true"
          >
            {{ $t("channel.contests.raffle.actions.publish") }}
          </ContestButton>

          <ContestTooltip
            side="bottom"
            :disabled="(raffle.user_participation ?? 0) > 0"
          >
            <template #trigger>
              <ContestButton
                v-if="raffle.status === ContestStatus.Ready"
                :priority="priority"
                :disabled="(raffle.user_participation ?? 0) === 0"
                :is-loading="loading || loadingEndRaffle"
                @click="showEndConfirmation = true"
              >
                {{ $t("channel.contests.raffle.actions.end") }}
              </ContestButton>
            </template>
            <p>
              <span>{{
                $t("channel.contests.raffle.status.noParticipants")
              }}</span>
            </p>
          </ContestTooltip>
        </div>
      </div>
    </div>

    <!-- User section -->
    <div
      class="h-16 p-4 justify-between items-center inline-flex w-full shadow-2xl"
      :class="{ 'rounded-b-3xl': !channelStore.isAsMonacoFanToken }"
    >
      <div
        class="w-full grow shrink basis-0 h-8 justify-between items-start flex"
      >
        <div class="flex-1 justify-start items-center gap-2 flex">
          <Avatar size="sm" :user="raffle.created_by ?? undefined" />
          <div
            class="text-text text-xs font-medium font-sans leading-[18px] line-clamp-1"
          >
            By {{ raffle.created_by?.username }}
          </div>
        </div>

        <div class="flex flex-row gap-2 shrink-0">
          <ContestTooltip side="bottom">
            <template #trigger>
              <div class="bg-transparent text-xl text-brand-primary size-11">
                <IconButtonV2
                  class="size-11"
                  icon="material-symbols:info-outline"
                />
              </div>
            </template>
            <p>
              <span>{{ tooltipLabel }}</span>
            </p>
          </ContestTooltip>

          <ContestItemDropdown
            v-if="channelStore.permissions.manageContests"
            object-name="Raffle"
            :is-share-enabled="raffle.status !== ContestStatus.Draft"
            @delete="showDeleteConfirmation = true"
            @edit="onEditClick"
            @share="onShareClick"
          />
          <IconButtonV2
            v-else
            :disabled="raffle.status === ContestStatus.Draft"
            icon="mdi:ios-share"
            @click="onShareClick"
          />
        </div>
      </div>
    </div>

    <DangerConfirmationModal
      v-model="showEndConfirmation"
      :title="$t('channel.contests.raffle.confirmations.end.title')"
      :confirm-label="$t('channel.contests.raffle.confirmations.end.confirm')"
      @confirm="onEnd"
    />

    <DangerConfirmationModal
      v-model="showPublishConfirmation"
      :title="$t('channel.contests.raffle.confirmations.publish.title')"
      :confirm-label="
        $t('channel.contests.raffle.confirmations.publish.confirm')
      "
      @confirm="onPublish"
    />

    <DangerConfirmationModal
      v-model="showDeleteConfirmation"
      :title="$t('channel.contests.raffle.confirmations.delete.title')"
      :confirm-label="
        $t('channel.contests.raffle.confirmations.delete.confirm')
      "
      @confirm="onDeleteRaffle"
    />
  </div>
</template>

<script lang="ts">
import { graphql as gql } from "@/generated";
import type { ResultOf } from "@graphql-typed-document-node/core";

export const RaffleItemCardFragment = gql(/* GraphQL */ `
  fragment RaffleItemCardData on Raffle {
    id
    __typename
    status
    title
    description
    end_time
    scheduled_end_time
    created_by_id
    has_joined
    user_participation
    ...RaffleJoinData
    winner_users {
      user {
        id
        username
        avatar
        is_verified
        locale
      }
    }
    prize {
      id
      title
      description
      image
      contest_prize_translations {
        title
        description
      }
    }
    created_by {
      id
      username
      avatar
      is_verified
      locale
    }
    raffle_translations {
      title
      description
    }
    raffle_communities {
      community {
        id
        name
      }
    }
    authorization_rule_group {
      id
      ...AuthorizationRuleGroupForGating
    }
  }
`);

export type RaffleItemCardData = ResultOf<typeof RaffleItemCardFragment>;
</script>

<script setup lang="ts">
import {
  ContestStatus,
  DeleteRaffleDocument,
  EndRaffleDocument,
  StartRaffleFromChannelDocument,
} from "@/generated/graphql";
import { computed, ref, useTemplateRef, defineAsyncComponent } from "vue";
import ContestFinished from "./contest-finished.vue";
import { ContestType, type IContestFinished } from "./contest.types";
import RaffleJoin, { type RaffleJoinData } from "./raffle-join.vue";
import { useInterval } from "@/composables/use-safe-set-interval";
import { CardPriority } from "./creation/contests";
import { useUserStore } from "@/stores/user.store";
import { RouteNames, router } from "@/router/app.router";
import { useMutation } from "@urql/vue";
import {
  useProvideGatingPermissions,
  type AuthorizationRuleGroupForGatingType,
} from "@/features/gating/use-gating-permissions";
import {
  EventCategory,
  TrackEventEnum,
} from "@/services/tracking/tracking-types";
import { useTrackingStore } from "@/stores/tracking.store";
import { useChannelStore } from "@/stores/channel.store";
import { ExperienceAction, ExperienceType } from "@/enums/experience-type.enum";
import IconButtonV2 from "@/components/ui/button/icon-button-v2.vue";
import { useShareReferral } from "@/composables/use-share-referral";
import Avatar from "@/components/me/avatar.vue";
import ContestItemDropdown from "./contest-item-dropdown.vue";
import ContestTooltip from "./contest-tooltip.vue";
import { useObjectTranslation } from "@/stores/translation.store";
import ContestButton from "./contest-button.vue";
import { RaffleWinnerUserFragment } from "@/components/show/contests/raffle-winner.vue";
import { useToastStore } from "@/stores/toast.store";
import { useI18n } from "vue-i18n";

type RaffleWinnerUserData = ResultOf<typeof RaffleWinnerUserFragment>;

// Lazy load
const DangerConfirmationModal = defineAsyncComponent(
  () => import("@/components/show/modals/danger-confirmation-modal.vue"),
);

const trackingService = useTrackingStore();

const emit = defineEmits<{
  refetch: [];
}>();

const props = defineProps<{
  raffle: RaffleItemCardData;
  authorizationRuleGroupFragment: AuthorizationRuleGroupForGatingType;
  variant?: "responsive" | "regular";
  loading: boolean;
}>();

useProvideGatingPermissions(
  computed(() => props.authorizationRuleGroupFragment),
);

const userStore = useUserStore();

const showEndConfirmation = ref(false);
const showPublishConfirmation = ref(false);
const showDeleteConfirmation = ref(false);

const finishedData = computed(() => {
  const winner = props.raffle.winner_users?.[0];
  const prize = props.raffle.prize;
  const end_time = props.raffle.end_time;

  if (!winner || !prize || !end_time) {
    return null;
  }

  return {
    winner: winner.user,
    prize: {
      id: prize.id,
      title: prize.title,
      image: prize.image,
      translations: props.raffle.prize?.contest_prize_translations.map(
        (t) => t.title ?? t.description,
      ),
    },
    end_time,
    created_by_id: props.raffle.created_by_id ?? props.raffle.created_by?.id,
    translations: props.raffle.raffle_translations.map(
      (t) => t.title ?? t.description,
    ),
    title: props.raffle.title ?? props.raffle.description,
    type: ContestType.Raffle,
  } as IContestFinished;
});

const { text: tooltipLabel } = useObjectTranslation(
  computed(() => {
    const originalText = props.raffle.description ?? props.raffle.title;
    if (!originalText) return null;

    return {
      authorUserId: props.raffle.created_by_id ?? props.raffle.created_by?.id,
      originalText: originalText,
      objectAccessors: {
        objectId: props.raffle.id,
        objectProperty: ["description", "title"],
      },
      translatedText:
        props.raffle.raffle_translations[0]?.description ??
        props.raffle.raffle_translations[0]?.title,
    };
  }),
);

const expired = ref(false);

const secondsTilEnd = ref<number | null>(null);
useInterval(() => {
  if (!props.raffle.scheduled_end_time) {
    return;
  }

  const endTime = new Date(props.raffle.scheduled_end_time);
  const now = new Date();
  const utcNow = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds(),
      now.getUTCMilliseconds(),
    ),
  );
  const diffTime = endTime.getTime() - utcNow.getTime();

  expired.value = diffTime < 0;

  secondsTilEnd.value = Math.ceil(diffTime / 1000);
}, 1000);

const countdownData = computed(() => {
  if (!secondsTilEnd.value)
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };

  const days = Math.floor(secondsTilEnd.value / (60 * 60 * 24));
  const hours = Math.floor((secondsTilEnd.value % (60 * 60 * 24)) / (60 * 60));
  const minutes = Math.floor((secondsTilEnd.value % (60 * 60)) / 60);
  const seconds = Math.floor(secondsTilEnd.value % 60);

  return { days, hours, minutes, seconds };
});

const priority = computed<CardPriority>(() => {
  if (
    !props.raffle.scheduled_end_time ||
    !countdownData.value ||
    props.raffle.status === ContestStatus.Ended
  )
    return CardPriority.Normal;

  if (new Date(props.raffle.scheduled_end_time) < new Date())
    return CardPriority.Unavailable;

  if (countdownData.value?.days && countdownData.value?.days >= 1) {
    return CardPriority.Normal;
  } else if (countdownData.value?.hours && countdownData.value?.hours >= 1) {
    return CardPriority.Incoming;
  } else {
    return CardPriority.Urgent;
  }
});

const channelStore = useChannelStore();
const showCreatorControls = computed(() => {
  if (!userStore.user) return false;

  const isOwner = props.raffle.created_by_id === userStore.userId;
  const isChannelAdmin = channelStore.permissions.manageChannel;

  return (
    (props.raffle.status === ContestStatus.Draft ||
      props.raffle.status === ContestStatus.Ready) &&
    (isOwner || isChannelAdmin)
  );
});

function onEditClick() {
  router.push({
    name: RouteNames.EditRaffle,
    params: { raffle: props.raffle.id, channel: channelStore.channelSlug },
  });
}

const _submitActionLabel = computed(() =>
  props.raffle.status === ContestStatus.Draft ? "publish" : "end raffle",
);

const { executeMutation: startRaffle, fetching: loadingStartRaffle } =
  useMutation(StartRaffleFromChannelDocument);

const { executeMutation: endRaffle, fetching: loadingEndRaffle } =
  useMutation(EndRaffleDocument);

const toastStore = useToastStore();
const { t } = useI18n();

/**
 * The raffle is exclusively claimed by a live show's stage. Channel-page
 * mutations (endRaffle / finishRaffleManually / deleteRaffle) refuse to act on
 * it — it must be ended from that show's producer controls. Surface a clear
 * error toast with a "Go to the show" action that deep-links to the claiming
 * show.
 */
function showRaffleClaimedByOtherShowToast(claimingShowId: string) {
  toastStore.addToast(
    {
      type: "error",
      title: t("channel.contests.raffle.claimedByOtherShow.title"),
      message: t("channel.contests.raffle.claimedByOtherShow.message"),
      persistent: true,
      closable: true,
      action: {
        label: t("channel.contests.raffle.claimedByOtherShow.goToShow"),
        onClick: () => {
          router.push({
            name: RouteNames.Show,
            params: {
              channel: channelStore.channelSlug,
              show: claimingShowId,
            },
          });
        },
      },
    },
    10000,
  );
}

async function onPublish() {
  showPublishConfirmation.value = false;
  if (props.raffle.status === ContestStatus.Draft) {
    try {
      await startRaffle({ raffle_id: props.raffle.id });
    } catch (e) {
      console.error(e);
    }
    emit("refetch");
    return;
  }
}

async function onEnd() {
  showEndConfirmation.value = false;
  if (props.raffle.status === ContestStatus.Ready) {
    try {
      const res = await endRaffle({ raffle_id: props.raffle.id });
      const endResult = res?.data?.endRaffle;

      // The raffle is staged on a live show's stage — it must be ended from
      // that show's producer controls, not the channel page.
      if (endResult?.__typename === "RaffleClaimedByOtherShowError") {
        showRaffleClaimedByOtherShowToast(endResult.claiming_show_id);
        return;
      }

      const winnerUsers =
        endResult && "winner_users" in endResult
          ? endResult.winner_users
          : null;
      trackingService.track({
        event: TrackEventEnum.ChannelRaffleWon,
        category: EventCategory.ChannelRaffle,
        metadata: {
          prize: props.raffle?.prize?.title ?? "",
          winnerIds:
            winnerUsers?.map((wu) => (wu.user as RaffleWinnerUserData).id) ??
            [],
          winnerUsernames:
            winnerUsers?.map(
              (wu) => (wu.user as RaffleWinnerUserData).username,
            ) ?? [],
          experienceId: props.raffle?.id,
          experienceType: ExperienceType.Raffle,
          experienceAction: ExperienceAction.RAFFLE_JOIN,
          experienceTitle: props.raffle?.title ?? "",
          experienceCreatorId: props.raffle?.created_by?.id,
          experienceCreatorUsername: props.raffle?.created_by?.username,
          // experienceMembership: channelStore.channelMemberships.find(
          //   (cm) =>
          //     cm.authorization_rule_id === props.raffle?.authorization_rule_id,
          // )?.name,
          experienceCommunityIds: props.raffle?.raffle_communities?.map(
            (rc) => rc.community.id,
          ),
          experienceCommunityNames: props.raffle?.raffle_communities
            ?.map((rc) => rc.community.name)
            .filter((name): name is string => name != null),
        },
      });
    } catch (e) {
      console.error(e);
    }
    emit("refetch");
    return;
  }
}

const { executeMutation: deleteRaffle } = useMutation(DeleteRaffleDocument);
async function onDeleteRaffle() {
  showDeleteConfirmation.value = false;
  // additionalTypenames is passed as the context (second arg directly, not wrapped).
  // URQL invalidates all active queries that selected a Raffle typename — including
  // GetRafflesForChannel — so the deleted raffle disappears without a manual cache evict.
  const res = await deleteRaffle(
    { raffle_id: props.raffle.id },
    { additionalTypenames: ["Raffle"] },
  );

  // deleteRaffle now returns a DeleteRaffleResult union: a raffle staged on a
  // live show's stage cannot be deleted from the channel page.
  const deleteResult = res?.data?.deleteRaffle;
  if (deleteResult?.__typename === "RaffleClaimedByOtherShowError") {
    showRaffleClaimedByOtherShowToast(deleteResult.claiming_show_id);
  }
}

const onShareClick = useShareReferral({
  openGraphObject: computed(() => ({
    title: props.raffle.description,
    image: props.raffle.prize?.image,
  })),
  referralObject: computed(() => ({
    title: props.raffle.title ?? "Earn exclusive rewards in FanFest",
    payload: {
      experience_id: props.raffle.id,
      experience_type: ExperienceType.Raffle,
      community_id:
        props.raffle.raffle_communities?.length === 1
          ? props.raffle.raffle_communities.at(0)?.community.id
          : (channelStore.selectedCommunity ?? undefined),
    },
  })),
});

const canJoinRaffle = computed(() => {
  return (
    !showCreatorControls.value &&
    !props.raffle.has_joined &&
    props.raffle.status === ContestStatus.Ready &&
    !expired.value
  );
});

const formatter = computed(
  () =>
    new Intl.DateTimeFormat(userStore.userLocale, {
      month: "long",
      day: "numeric",
    }),
);

const prettyEndTime = computed(() => {
  if (!finishedData.value) return "No finishedData.value";
  const date = new Date(finishedData.value.end_time);
  return formatter.value.format(date);
});

const raffleJoinRef = useTemplateRef("raffleJoin");

// TODO: THIS IS SOME WEIRD SPAGHETTI CODE, WE DID THIS FOR ADAM ON A LATE DAY BEFORE GOING HOME, WE NEED TO MOVE THIS CODE SO THAT WE JOIN FROM THE RAFFLE REFERRAL (MODAL) COMPONENT AND THEN REFETCH THE RAFFLE CARD
function joinRaffle() {
  if (raffleJoinRef.value) {
    raffleJoinRef.value.joinRaffle();
  } else {
    console.error("[RaffleItemCard] Unable to join raffle, ref is null");
  }
}

defineExpose({
  joinRaffle,
});
</script>
